import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Car,
  Truck,
  Bike,
  CarFront,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useTranslation } from '@/i18n/hooks/useTranslation';

// Tipos de vehículos comunes con iconos de Lucide
export const commonVehicleTypes = [
  {
    id: 'all',
    label: 'Todos',
    icon: <RefreshCw size={16} className='text-current' />,
  },
  {
    id: 'suv',
    label: 'SUVs',
    icon: <Car size={16} className='text-current' />,
  },
  {
    id: 'sedan',
    label: 'Sedanes',
    icon: <Car size={16} className='text-current' />,
  },
  {
    id: 'hatchback',
    label: 'Hatchbacks',
    icon: <CarFront size={16} className='text-current' />,
  },
  {
    id: 'pickup',
    label: 'Pickups',
    icon: <Truck size={16} className='text-current' />,
  },
  // `Camioneta` es una categoría propia del catálogo, distinta de `Pickup`, y
  // tiene su propia pestaña: agruparlas escondía cuál de las dos era cada auto
  // y hacía imposible filtrar una sola. Ver `mappings` más abajo.
  {
    id: 'camioneta',
    label: 'Camionetas',
    icon: <Truck size={16} className='text-current' />,
  },
  {
    id: 'van',
    label: 'Vans',
    icon: <Truck size={16} className='text-current' />,
  },
  {
    id: 'coupe',
    label: 'Coupés',
    icon: <Car size={16} className='text-current' />,
  },
  {
    id: 'wagon',
    label: 'Wagons',
    icon: <CarFront size={16} className='text-current' />,
  },
  // Ticket #97: las camionetas de faena son una categoría propia del catálogo
  // ("Vehículos de trabajo"), no una variante de Pickup: el pedido era
  // justamente poder verlas aparte de las de uso particular.
  {
    id: 'trabajo',
    label: 'Vehículos de trabajo',
    icon: <Truck size={16} className='text-current' />,
  },
];

// Esta función mapea los tipos comunes a los tipos reales en la base de datos
export const mapCommonTypeToActual = (
  commonType: string,
  availableTypes: string[]
): string[] => {
  const mappings: Record<string, string[]> = {
    suv: ['SUV', 'Crossover', 'Todoterreno', '4x4'],
    sedan: ['Sedán', 'Sedan', 'Berlina'],
    hatchback: ['Hatchback', 'Compacto'],
    pickup: ['Pickup'],
    camioneta: ['Camioneta'],
    van: ['Van', 'Minivan', 'Furgoneta'],
    coupe: ['Coupe', 'Coupé', 'Deportivo'],
    wagon: ['Wagon', 'Station Wagon', 'Familiar'],
    trabajo: ['Vehículos de trabajo', 'Vehiculos de trabajo', 'Vehículo de trabajo', 'Vehiculo de trabajo'],
  };

  if (commonType === 'all') {
    return [];
  }

  // Encuentra los tipos que coinciden en la base de datos
  const potentialTypes = mappings[commonType] || [];
  return availableTypes.filter((type) =>
    potentialTypes.some((pt) => type.toLowerCase().includes(pt.toLowerCase()))
  );
};

/**
 * Las pestañas que pueden dar resultados (ticket #86).
 *
 * La lista de tipos es fija (SUVs, Sedanes, Pickups…), pero el catálogo puede
 * estar acotado a ciertas categorías o el tenant puede no tener stock de un
 * tipo. Ofrecer una pestaña que siempre devuelve cero es una promesa falsa, y
 * en una página de solo camionetas es directamente confuso.
 *
 * `all` se conserva siempre; las demás sobreviven si mapean a algo que hay.
 */
export const tiposDeVehiculoConStock = (availableTypes: string[]) =>
  commonVehicleTypes.filter(
    (t) => t.id === 'all' || mapCommonTypeToActual(t.id, availableTypes).length > 0
  );

/**
 * `false` sólo cuando no hay NINGUNA categoría que ofrecer: una barra con un
 * único botón "Todos" sobre un catálogo vacío es ruido.
 *
 * Con una sola categoría la barra SÍ se dibuja («Todos» + esa categoría), aunque
 * las dos muestren lo mismo: en una página dedicada dice de un vistazo qué tipo
 * de vehículo lista, y su ausencia se lee como que algo falta.
 */
export const laBarraDeTiposAporta = (availableTypes: string[]) =>
  tiposDeVehiculoConStock(availableTypes).length > 1;

interface VehicleTypeFilterProps {
  activeVehicleType: string;
  setActiveVehicleType: (type: string) => void;
  availableTypes: string[];
  setSelectedTypes: React.Dispatch<React.SetStateAction<string[]>>;
  filterButtonColors?: {
    buttonBgColor: string;
    buttonTextColor: string;
    buttonBorderColor: string;
    activeButtonBgColor: string;
    activeButtonTextColor: string;
    activeButtonBorderColor: string;
    containerBgColor: string;
    containerBorderColor: string;
  }[];
}

export const VehicleTypeFilter: React.FC<VehicleTypeFilterProps> = ({
  activeVehicleType,
  setActiveVehicleType,
  availableTypes,
  setSelectedTypes,
  filterButtonColors = [
    {
      buttonBgColor: '#ffffff',
      buttonTextColor: '#3b82f6',
      buttonBorderColor: '#3b82f6',
      activeButtonBgColor: '#3b82f6',
      activeButtonTextColor: '#ffffff',
      activeButtonBorderColor: '#3b82f6',
      containerBgColor: '#ffffff',
      containerBorderColor: '#e5e7eb',
    },
  ],
}) => {
  const { t } = useTranslation();
  // Obtener la configuración de colores (siempre será el primer elemento)
  const buttonColors = filterButtonColors[0] || filterButtonColors[0];

  // Sólo las pestañas que pueden dar resultados; «Todos» se conserva siempre y
  // muestra todo lo que el catálogo permite, no el inventario del cliente.
  const tiposConStock = tiposDeVehiculoConStock(availableTypes);

  // Sin ninguna categoría que ofrecer no se dibuja (ver `laBarraDeTiposAporta`).
  if (!laBarraDeTiposAporta(availableTypes)) return null;

  // Filter by vehicle type
  const handleVehicleTypeClick = (typeId: string) => {
    // Si ya estaba activo o se selecciona "Todos", quitamos todos los filtros de tipo
    if (typeId === 'all' || typeId === activeVehicleType) {
      setSelectedTypes([]);
      setActiveVehicleType('all');
      return;
    }

    // Encuentra los tipos correspondientes en la base de datos
    const matchingTypes = mapCommonTypeToActual(typeId, availableTypes);

    // Si existen, aplicamos el filtro
    if (matchingTypes.length > 0) {
      setSelectedTypes(matchingTypes);
      setActiveVehicleType(typeId);
    }
  };

  const renderScrollButtons = (scrollArea: HTMLDivElement) => {
    const hasOverflow = scrollArea?.scrollWidth > scrollArea?.clientWidth;

    if (!hasOverflow) return null;

    return (
      <>
        <button
          onClick={() =>
            scrollArea.scrollBy({ left: -200, behavior: 'smooth' })
          }
          className='absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 rounded-full p-1 shadow-md'
          aria-label={t('site.vehicles.scrollLeft')}
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => scrollArea.scrollBy({ left: 200, behavior: 'smooth' })}
          className='absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 rounded-full p-1 shadow-md'
          aria-label={t('site.vehicles.scrollRight')}
        >
          <ChevronRight size={20} />
        </button>
      </>
    );
  };

  return (
    <div
      className='w-full mb-6 rounded-lg shadow-sm p-3 relative'
      style={{
        backgroundColor: buttonColors.containerBgColor,
        border: `1px solid ${buttonColors.containerBorderColor}`,
      }}
    >
      <ScrollArea className='w-full relative'>
        <div className='flex py-1 px-4 gap-2 items-center overflow-x-auto'>
          {tiposConStock.map((type) => {
            const isActive = activeVehicleType === type.id;

            return (
              <Button
                key={type.id}
                variant={isActive ? 'default' : 'outline'}
                size='sm'
                style={{
                  backgroundColor: isActive
                    ? buttonColors.activeButtonBgColor
                    : buttonColors.buttonBgColor,
                  color: isActive
                    ? buttonColors.activeButtonTextColor
                    : buttonColors.buttonTextColor,
                  borderColor: isActive
                    ? buttonColors.activeButtonBorderColor
                    : buttonColors.buttonBorderColor,
                }}
                className={`
                  flex items-center gap-2 px-4 py-2 min-w-max
                  transition-all duration-200 ease-in-out rounded-full
                  ${isActive ? 'shadow-md' : 'hover:opacity-80'}
                `}
                onClick={() => handleVehicleTypeClick(type.id)}
              >
                <span
                  style={{
                    color: isActive
                      ? buttonColors.activeButtonTextColor
                      : buttonColors.buttonTextColor,
                  }}
                  className='transition-colors'
                >
                  {type.icon}
                </span>
                <span
                  style={{
                    color: isActive
                      ? buttonColors.activeButtonTextColor
                      : buttonColors.buttonTextColor,
                  }}
                  className='text-sm font-medium'
                >
                  {/* La etiqueta visible sale del i18n por id; `label` queda como
                      el texto de siempre (es idéntico al de es/site.ts). */}
                  {t(`site.vehicles.types.${type.id}`)}
                </span>
              </Button>
            );
          })}
        </div>
        <ScrollBar orientation='horizontal' className='h-1.5' />
      </ScrollArea>
    </div>
  );
};
